import React from 'react';
import PropTypes from 'prop-types';
import { Header } from 'semantic-ui-react';

import Carousel from '../../darkmatter/Carousel';
import IndicatorGroup from './components/IndicatorGroup';
import CarouselArrow from './components/CarouselArrow';
import ImageGrid from './components/ImageGrid';
import { GatsbyImagePropType } from '../../services/proptypes';
import './ImageCollection.css';

export default class ImageCollection extends React.Component {
  static createChunks(list, chunkSize, uniformChunks = false) {
    const imageChunks = list.reduce(
      (acc, _, i) => (i % chunkSize ? acc : [...acc, list.slice(i, i + chunkSize)]),
      [],
    );

    if (uniformChunks) {
      return imageChunks.filter(chunk => chunk.length === imageChunks[0].length)
    }

    return imageChunks;
  }

  createImageRows() {
    const { numberOfRowDisplayItems, images, cutUnevenRows, smartImageGrouping } = this.props;

    if (images.length < numberOfRowDisplayItems) {
      return [images];
    }

    let groupedImages = [];
    if (smartImageGrouping) {
      // The last 2 rows will have less than numberOfRowDisplayItems
      // The idea is to have 2 rows divided evenly with numberOfRowDisplayItems
      // + (images.length % numberOfRowDisplayItems) images
      const remainingImages = numberOfRowDisplayItems + (images.length % numberOfRowDisplayItems);
      const imagesToDivideInRowsEvenly = images.length - remainingImages;
      const idxOfSlice = imagesToDivideInRowsEvenly + Math.ceil(remainingImages / 2);
      groupedImages = ImageCollection.createChunks(
        images.slice(0, imagesToDivideInRowsEvenly),
        numberOfRowDisplayItems,
      );

      return groupedImages
        .concat([images.slice(imagesToDivideInRowsEvenly, idxOfSlice)])
        .concat([images.slice(idxOfSlice)]);
    } else {
      return ImageCollection.createChunks(images, numberOfRowDisplayItems, cutUnevenRows);
    }
  }

  renderCarousel(overrides = {}) {
    const { images, numberOfRowDisplayItems, disable, carouselOptions } = this.props;
    const { numberOfRowDisplayItemsOR, disableOR } = overrides;
    const finalDisable = disableOR || disable;
    const finalNumberOfRowDisplayItems = numberOfRowDisplayItemsOR || numberOfRowDisplayItems;
    const { autoScroll, autoScrollTime } = carouselOptions;
    const imageLength = images.length;
    return (
      <Carousel
        totalNumberOfItems={images.length}
        autoScroll={autoScroll}
        autoScrollTime={autoScrollTime}
        numberOfDisplayItems={
          finalNumberOfRowDisplayItems > imageLength ? imageLength : finalNumberOfRowDisplayItems
        }
      >
        {({ currentIndex, changeCurrentIndex, getDisplayItemIndices }) => (
          <div style={{ margin: 'auto', textAlign: 'center' }}>
            <ImageGrid
              imageGroups={[getDisplayItemIndices().map(index => images[index])]}
              rightArrow={
                finalDisable !== 'arrows' &&
                finalDisable !== 'both' && (
                  <CarouselArrow
                    orientation="right"
                    onToggle={changeCurrentIndex}
                    currentIndex={currentIndex}
                  />
                )
              }
              leftArrow={
                finalDisable !== 'arrows' &&
                finalDisable !== 'both' && (
                  <CarouselArrow
                    orientation="left"
                    onToggle={changeCurrentIndex}
                    currentIndex={currentIndex}
                  />
                )
              }
            />
            {finalDisable !== 'indicators' && finalDisable !== 'both' && (
              <IndicatorGroup
                images={images}
                currentIndex={currentIndex}
                changeCurrentIndex={changeCurrentIndex}
              />
            )}
          </div>
        )}
      </Carousel>
    );
  }

  renderGallery() {
    const imageGroups = this.createImageRows();
    return <ImageGrid imageGroups={imageGroups} />;
  }

  render() {
    const { as, centered, header, className, ignoreMobileCarousel } = this.props;
    const wrapperClassName = `imagecollection-wrapper ${className}`;

    return (
      <div
        className={wrapperClassName}
        style={centered ? { margin: 'auto', textAlign: 'center' } : {}}
      >
        {header && <Header as="h1">{header}</Header>}
        <span className="imagecollection-desktop">
          {as === 'gallery' ? this.renderGallery() : this.renderCarousel()}
        </span>
        <span className="imagecollection-mobile">
          {ignoreMobileCarousel
            ? this.renderGallery()
            : this.renderCarousel({
                numberOfRowDisplayItemsOR: 1,
                disable: 'none',
              })}
        </span>
      </div>
    );
  }
}

ImageCollection.propTypes = {
  carouselOptions: PropTypes.shape({
    autoScroll: PropTypes.bool,
    autoScrollTime: PropTypes.number,
  }),
  as: PropTypes.oneOf(['carousel', 'gallery']),
  centered: PropTypes.bool,
  className: PropTypes.string,
  cutUnevenRows: PropTypes.bool,
  disable: PropTypes.oneOf(['none', 'indicators', 'arrows', 'both']),
  header: PropTypes.string,
  images: PropTypes.oneOf(
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.arrayOf(GatsbyImagePropType),
  ).isRequired,
  smartImageGrouping: PropTypes.bool,
  numberOfRowDisplayItems: PropTypes.number,
  ignoreMobileCarousel: PropTypes.bool,
};

ImageCollection.defaultProps = {
  as: 'gallery',
  carouselOptions: { autoScroll: undefined, autoScrollTime: undefined },
  centered: false,
  className: '',
  cutUnevenRows: true,
  disable: 'none',
  header: '',
  numberOfRowDisplayItems: 3,
  ignoreMobileCarousel: false,
  smartImageGrouping: true,
};

ImageCollection.propOptions = {};

ImageCollection.dataPropsMap = {
  images: ['business', 'gallery_images'],
};
